import { useEffect, useRef } from 'react';
import { useLoginVerifyQuery } from '../../__generated__/chirp/com/hubspot/portals/platform/app/rpc/AccessRpc.hooks';
import type { LoginVerifyContactResponse } from '../../__generated__/chirp/com/hubspot/portals/platform/app/rpc/AccessRpc';
import { getPortalAccountId, getPortalInstanceId } from '../utils/metadata';
import { usePortalsPlatformObjectId } from '../context/PortalsPlatformObjectIdContext';
import { isAccessDeniedError } from '../utils/portalAuthErrors';
import { redirectToLogin } from '../utils/loginRedirect';

export type UsePortalsAuthOptions = {
  redirectUrl?: string;
};

export type UsePortalsAuthResult = {
  isLoading: boolean;
  isAccessDenied: boolean;
  user: LoginVerifyContactResponse | undefined;
};

export function usePortalsAuth(
  options?: UsePortalsAuthOptions
): UsePortalsAuthResult {
  const accountId = getPortalAccountId();
  const portalInstanceId = getPortalInstanceId();
  const objectId = usePortalsPlatformObjectId();

  const {
    data: user,
    loading,
    error,
  } = useLoginVerifyQuery({
    variables: { accountId, portalInstanceId, objectId },
  });

  const isAccessDenied = !loading && !user && isAccessDeniedError(error);
  const hasRedirectedRef = useRef(false);

  useEffect(() => {
    if (!loading && !user && !isAccessDenied && !hasRedirectedRef.current) {
      hasRedirectedRef.current = true;
      redirectToLogin(options?.redirectUrl);
    }
  }, [loading, user, isAccessDenied, options?.redirectUrl]);

  return { isLoading: loading, isAccessDenied, user };
}
